/**
 * OnboardingRouter Component
 * 
 * Routes users to appropriate onboarding flow based on:
 * 1. Whether they're logging in for the first time
 * 2. Whether their organization has existing connections
 * 
 * Flow Decision Tree:
 * - First time user + No connections → Connection Setup Flow (/onboarding/*)
 * - First time user + Has connections → App Orientation Flow (/welcome/*)
 * - Returning user → Main Dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { connectionDetection } from '../../services/connectionDetectionService';
import { userAPI } from '../../api/enhancedApiService';
import LoadingSpinner from '../common/LoadingSpinner';
import { apiCircuitBreakers } from '../../utils/circuitBreaker';
import { secureLogger } from '../../utils/secureLogger';

const OnboardingRouter = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [routingDecision, setRoutingDecision] = useState(null);
  const hasProcessedUser = useRef(null);
  const sessionComplete = useRef(false);

  useEffect(() => {
    const determineRoute = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // If session is complete, don't process again
      if (sessionComplete.current) {
        setLoading(false);
        return;
      }

      // Prevent multiple simultaneous calls for the same user
      if (hasProcessedUser.current === user.id || routingDecision) {
        setLoading(false);
        return;
      }

      // Another pass is already running the routing decision for this user
      // (typically a StrictMode dev double-effect). Bail without touching
      // loading state — the in-flight pass owns it and will flip loading=false
      // once it sets routingDecision. Setting loading=false here would cause
      // a flash where children render before the redirect fires.
      if (hasProcessedUser.current === `processing_${user.id}`) {
        return;
      }

      hasProcessedUser.current = `processing_${user.id}`;

      try {
        // Check if this is a first-time login
        const isFirstLogin = await checkIfFirstLogin(user);

        if (!isFirstLogin) {
          // Returning user - go to dashboard
          const decision = { type: 'dashboard', reason: 'returning_user' };
          setRoutingDecision(decision);
          hasProcessedUser.current = user.id; // Mark as completed
          sessionComplete.current = true; // Mark session as complete
          setLoading(false);
          return;
        }

        // First-time user - check if organization has connections
        const connectionTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection check timeout')), 8000)
        );
        
        const hasConnections = await apiCircuitBreakers.connections.call(
          () => Promise.race([
            connectionDetection.hasConnections({ forceFresh: true }),
            connectionTimeoutPromise
          ]),
          () => {
            return false; // Safe fallback - route to onboarding
          }
        );

        if (hasConnections) {
          // First-time user already has a connection — skip the duplicative
          // /welcome flow (it has its own bugs around NaN time-spent, broken
          // links, and missing first-login marking) and go straight to the
          // dashboard. OnboardingDashboardPage marks first_login_completed
          // when the user exits the wizard, so subsequent visits won't
          // re-enter this branch anyway.
          setRoutingDecision({
            type: 'dashboard',
            reason: 'first_login_with_connections',
          });
          hasProcessedUser.current = user.id;
          sessionComplete.current = true;
        } else {
          // No connections - send to connection setup
          setRoutingDecision({ 
            type: 'onboarding', 
            reason: 'first_login_no_connections' 
          });
          hasProcessedUser.current = user.id; // Mark as completed
          sessionComplete.current = true; // Mark session as complete
        }

        // Note: Don't mark first login complete here - let the actual flows handle that

      } catch (error) {
        secureLogger.error('[OnboardingRouter] Error determining route:', error);
        
        // Check if this is a network/CORS error that prevents API communication
        const isNetworkError = error.message?.includes('Network Error') || 
                               error.code === 'ERR_NETWORK' ||
                               error.message?.includes('CORS');
        
        if (isNetworkError) {
          // If we can't communicate with the API, let the user access the normal app
          // This prevents getting stuck in onboarding due to temporary API issues
          setRoutingDecision({ 
            type: 'dashboard', 
            reason: 'network_error_bypass' 
          });
          hasProcessedUser.current = user.id; // Mark as completed
          sessionComplete.current = true; // Mark session as complete
        } else {
          // For other errors (business logic, etc.), default to connection setup
          setRoutingDecision({ 
            type: 'onboarding', 
            reason: 'error_fallback' 
          });
          hasProcessedUser.current = user.id; // Mark as completed
          sessionComplete.current = true; // Mark session as complete
        }
      }

      setLoading(false);
    };

    determineRoute();
  }, [user?.id]); // Only re-run if user ID changes - NEVER include routingDecision to prevent loops

  /**
   * Check if this is the user's first login
   * First checks user profile from database, falls back to localStorage for compatibility
   */
  const checkIfFirstLogin = async (user) => {
    try {
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('API timeout')), 10000)
      );
      
      // Check user profile from database with circuit breaker protection
      const profile = await apiCircuitBreakers.userProfile.call(
        () => Promise.race([
          userAPI.getProfile(null, { forceFresh: true }),
          timeoutPromise
        ]),
        () => {
          return null; // Fallback to localStorage check
        }
      );
      
      if (profile?.first_login_completed) {
        return false;
      }

      // If no database record, check localStorage for backward compatibility
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      if (hasCompletedOnboarding) {
        // Migrate localStorage state to database
        try {
          await userAPI.markFirstLoginComplete();
        } catch (migrationError) {
          // Migration failed, but continue
        }
        return false;
      }
      return true; // First login
    } catch (error) {
      secureLogger.error('[OnboardingRouter] Error checking first login status:', error);
      
      // Check if this is a network/CORS error or timeout
      const isNetworkError = error.message?.includes('Network Error') || 
                             error.code === 'ERR_NETWORK' ||
                             error.message?.includes('CORS') ||
                             error.message?.includes('timeout') ||
                             error.message?.includes('API timeout');
      
      if (isNetworkError) {
        // If we can't reach the API, assume this is a returning user to avoid onboarding loop
        return false;
      }
      
      // Fallback to localStorage check for non-network errors
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      const isFirstLogin = !hasCompletedOnboarding;
      return isFirstLogin;
    }
  };

  /**
   * Mark that the user has been through the initial routing
   * Note: This is now handled by the completion endpoints in the actual flows
   */
  const markFirstLoginComplete = async (user) => {
    try {
      // Update user profile in database
      await userAPI.markFirstLoginComplete();
    } catch (error) {
      secureLogger.error('[OnboardingRouter] Error updating database, falling back to localStorage:', error);
      // Fallback to localStorage
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    }
  };

  // Show loading while determining route. Also covers the gap between
  // loading=false and routingDecision being set, which can otherwise let
  // children flash on screen before the redirect fires.
  if (loading || (user && !routingDecision)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 text-center">Setting up your experience...</p>
        </div>
      </div>
    );
  }

  // No user - let normal auth flow handle it
  if (!user) {
    return children;
  }

  // Route based on decision - execute only once per user session
  if (routingDecision && sessionComplete.current) {
    switch (routingDecision.type) {
      case 'welcome':
        return <Navigate to="/welcome" replace />;

      case 'onboarding':
        return <Navigate to="/onboarding" replace />;

      case 'dashboard':
        // Returning user should see the children (DashboardPage)
        return children;

      default:
        secureLogger.warn('[OnboardingRouter] Unknown routing decision:', routingDecision);
        return children;
    }
  }

  // Default - render children (normal app)
  return children;
};

/**
 * Hook to get onboarding status information
 * Useful for debugging and analytics
 */
export const useOnboardingStatus = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const getStatus = async () => {
      if (!user) return;

      try {
        const [hasConnections, connectionCount, profile] = await Promise.allSettled([
          connectionDetection.hasConnections(),
          connectionDetection.getConnectionCount(),
          userAPI.getProfile()
        ]);

        // Handle profile data with fallback to localStorage
        let hasCompletedOnboarding = false;
        if (profile.status === 'fulfilled' && profile.value?.first_login_completed) {
          hasCompletedOnboarding = true;
        } else {
          // Fallback to localStorage check
          hasCompletedOnboarding = !!localStorage.getItem(`onboarding_completed_${user.id}`);
        }

        setStatus({
          userId: user.id,
          hasConnections: hasConnections.status === 'fulfilled' ? hasConnections.value : false,
          connectionCount: connectionCount.status === 'fulfilled' ? connectionCount.value : 0,
          hasCompletedOnboarding,
          organizationId: user.user_metadata?.organization_id,
          profileData: profile.status === 'fulfilled' ? profile.value : null
        });
      } catch (error) {
        console.error('[OnboardingStatus] Error getting status:', error);
      }
    };

    getStatus();
  }, [user]);

  return status;
};

export default OnboardingRouter;